/**
 * Signed-in purchase / upgrade of a Quixo Audit (G05 / G07). Posts to POST /v1/billing/portal-checkout with the tier and
 * either a website or one social profile; the server uses the session email + workspace and the catalogue price, and
 * answers a Stripe Checkout URL (the only place this form redirects to). Stripe returns to /audit[/<id>]?order=.
 */
import { useId, useRef, useState, type FormEvent } from 'react';
import { Button, Field, Select, Text, TextInput } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { bff } from '../../../lib/bff';
import { auditErrorKey, isStripeCheckout, subjectLabel } from './audit-view';

type Tier = 'essential' | 'pro' | 'enterprise';
type Platform = 'instagram' | 'facebook' | 'tiktok' | 'linkedin' | 'youtube' | 'x';

/** Body for portal-checkout from the form fields; null when the subject is not a usable https address. */
export function checkoutBody(o: { tier: Tier; kind: 'website' | 'social'; value: string; platform: Platform; currency: 'USD' | 'AED'; fromJobId?: string }) {
  const raw = o.value.trim();
  if (!raw) return null;
  const withScheme = /^https?:\/\//i.test(raw) ? raw.replace(/^http:\/\//i, 'https://') : o.kind === 'social' ? socialUrl(o.platform, raw) : `https://${raw}`;
  try { new URL(withScheme); } catch { return null; }
  const base = { tier: o.tier, currency: o.currency, ...(o.fromJobId ? { fromJobId: o.fromJobId } : {}) };
  return o.kind === 'website' ? { ...base, website: withScheme } : { ...base, profiles: [{ platform: o.platform, url: withScheme }] };
}

const PROFILE_BASE: Record<Platform, string> = {
  instagram: 'https://www.instagram.com/', facebook: 'https://www.facebook.com/', tiktok: 'https://www.tiktok.com/@', linkedin: 'https://www.linkedin.com/company/',
  youtube: 'https://www.youtube.com/@', x: 'https://x.com/',
};
/** Idempotency-Key (required by the API): one per distinct body, so a double click or a retry replays the first order. */
export function checkoutKey(prev: { body: string; key: string } | null, body: string): { body: string; key: string } {
  if (prev && prev.body === body) return prev;
  const key = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `buy-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return { body, key };
}

const socialUrl = (p: Platform, handle: string) => `${PROFILE_BASE[p]}${encodeURIComponent(handle.replace(/^@/, '').trim())}`;

export function BuyAudit({ defaultTier = 'pro', subject, fromJobId, tiers = ['essential', 'pro', 'enterprise'], compact = false }: {
  defaultTier?: Tier; subject?: string; fromJobId?: string; tiers?: readonly Tier[]; compact?: boolean;
}) {
  const { t } = useApp();
  const id = useId();
  const initialSocial = subject ? subjectLabel(subject).platform : null;
  const [tier, setTier] = useState<Tier>(tiers.includes(defaultTier) ? defaultTier : tiers[0]!);
  const [kind, setKind] = useState<'website' | 'social'>(initialSocial ? 'social' : 'website');
  const [platform, setPlatform] = useState<Platform>((initialSocial as Platform | null) ?? 'instagram');
  const [value, setValue] = useState(subject ?? '');
  const [currency, setCurrency] = useState<'USD' | 'AED'>('USD');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idem = useRef<{ body: string; key: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const body = checkoutBody({ tier, kind, value, platform, currency, ...(fromJobId ? { fromJobId } : {}) });
    if (!body) { setError(t(kind === 'website' ? 'shell.p1.buy.errWebsite' : 'shell.p1.buy.errHandle')); return; }
    setBusy(true); setError(null);
    try {
      const json = JSON.stringify(body);
      idem.current = checkoutKey(idem.current, json);
      const r = await bff<{ checkoutUrl?: string }>('/v1/billing/portal-checkout', { method: 'POST', body: json, headers: { 'idempotency-key': idem.current.key } });
      if (isStripeCheckout(r.checkoutUrl)) window.location.assign(r.checkoutUrl);
      else setError(t('shell.error.action'));
    } catch (err) {
      setError(t(auditErrorKey(err)));
    } finally { setBusy(false); }
  };

  return (
    <form className="qm-form qm-buy" onSubmit={(e) => void submit(e)} aria-describedby={`${id}-note`}>
      <div className="qm-buy__row">
        <Field label={t('shell.p1.buy.tier')}>
          {({ id: fid, describedBy }) => (
            <Select id={fid} aria-describedby={describedBy} value={tier} onChange={(e) => setTier(e.target.value as Tier)}>
              {tiers.map((x) => <option key={x} value={x}>{t(`shell.p1.tier.${x === 'pro' ? 'professional' : x}`)}</option>)}
            </Select>
          )}
        </Field>
        <Field label={t('shell.p1.buy.currency')}>
          {({ id: fid, describedBy }) => (
            <Select id={fid} aria-describedby={describedBy} value={currency} onChange={(e) => setCurrency(e.target.value as 'USD' | 'AED')}>
              <option value="USD">USD</option>
              <option value="AED">AED</option>
            </Select>
          )}
        </Field>
        {compact ? null : (
          <Field label={t('shell.p1.buy.kind')}>
            {({ id: fid, describedBy }) => (
              <Select id={fid} aria-describedby={describedBy} value={kind} onChange={(e) => setKind(e.target.value as 'website' | 'social')}>
                <option value="website">{t('shell.p1.buy.kindWebsite')}</option>
                <option value="social">{t('shell.p1.buy.kindSocial')}</option>
              </Select>
            )}
          </Field>
        )}
      </div>
      <div className="qm-buy__row">
        {kind === 'social' ? (
          <Field label={t('shell.p1.buy.platform')}>
            {({ id: fid, describedBy }) => (
              <Select id={fid} aria-describedby={describedBy} value={platform} onChange={(e) => setPlatform(e.target.value as Platform)}>
                {(Object.keys(PROFILE_BASE) as Platform[]).map((p) => <option key={p} value={p}>{t(`shell.p1.platform.${p}`)}</option>)}
              </Select>
            )}
          </Field>
        ) : null}
        <Field label={t(kind === 'website' ? 'shell.p1.buy.website' : 'shell.p1.buy.handle')} hint={t(kind === 'website' ? 'shell.p1.buy.websiteHint' : 'shell.p1.buy.handleHint')}>
          {({ id: fid, describedBy }) => <TextInput id={fid} aria-describedby={describedBy} dir="ltr" required value={value} inputMode={kind === 'website' ? 'url' : 'text'} autoComplete="off" onChange={(e) => setValue(e.target.value)} />}
        </Field>
      </div>
      <Text tone="muted" id={`${id}-note`}>{t('shell.p1.buy.note')}</Text>
      {error ? <Text role="alert">{error}</Text> : null}
      <Button type="submit" variant="primary" disabled={busy}>{fromJobId ? t('shell.p1.buy.upgrade') : t('shell.p1.buy.submit')}</Button>
    </form>
  );
}
